import axios from 'axios';

import {envConfig} from 'config';
import {
    AsyncRequestStatusName, AsyncRequestTypeName, SessionFeedback, AccountAssignmentInfo, ReassignDetails, SessionHistory,
    SessionActivityName, AnyParticipant
} from 'interfaces';
import {
    AssignmentAvailabilityData, ClientConnect, PaginatedData, SessionNoteAccess, TextChatMessage,
    User, VirtialBackgroundsData, VirtualBackgroundConfig
} from 'types';

import {API_URL} from './constants';

const actionTypes = {
    JOIN_SESSION: 'join_session',
    END_SESSION: 'end_session',
    GET_STATE: 'get_state',
    REASSIGN_SESSION: 'reassign_session',
    END_CALL: 'end_call',
    GET_SCREENSHOT: 'get_screenshot',
    DELETE_SCREENSHOT: 'delete_screenshot',
    DOWNLOAD_SCREENSHOT: 'download_screenshot',
    UPLOAD_SCREENSHOT: 'upload_screenshot',
    START_ACTIVITY: 'start_activity',
    RESTART_ACTIVITY: 'restart_activity',
    ADD_ACTIVITY_DATA: 'add_activity_data',
    GET_ACTIVITY_FILE: 'get_activity_file',
    STOP_ACTIVITY: 'stop_activity',
    START_ON_HOLD: 'start_on_hold',
    STOP_ON_HOLD: 'stop_on_hold',
    NOTIFY: 'notify',
    CREATE_ASYNC_REQUEST: 'create_async_request',
    UPDATE_ASYNC_REQUEST: 'update_async_request',
    GET_ASYNC_REQUEST_SCREENSHOT: 'get_async_request_screenshot',
    RESET_ASYNC_REQUEST: 'reset_async_request',
    SEND_PARTICIPANT_INVITE_NOTIFICATION: 'send_participant_invite_notification',
    GET_ASSIGNMENT_AVAILABILITY: 'get_assignment_availability',
    GET_USERS_AVAILABILITY: 'get_users_availability',
    ADD_FEEDBACK: 'add_feedback',
    GET_VIRTUAL_BACKGROUNDS: 'get_virtual_backgrounds',
    UPDATE_VIRTUAL_BACKGROUND: 'update_virtual_background',
    GET_AI_SUGGESTIONS: 'get_ai_suggestions',
    GET_CLIENT_TEXT_CHAT_HISTORY: 'get_client_text_chat_history',
    CREATE_CLIENT_CONNECT: 'create_client_connect',
    DELETE_CLIENT_CONNECT: 'delete_client_connect',
    ADD_NOTE: 'add_note',
    DELETE_NOTE: 'delete_note',
    GET_PREV_NOTES: 'get_prev_notes',
    CHANGE_ASSIGNMENT: 'change_assignment',
    UPDATE_PARTICIPANT_DATA: 'update_participant_data',
    INVITE_PARTICIPANTS: 'invite_participants',
    REMOVE_PARTICIPANT: 'remove_participant',
    LEAVE_SESSION: 'leave_session',
    DEESCALATE: 'deescalate',
    ASK_AGENT_CHATBOT: 'ask_agent_chatbot',
};

const executeAction = <T = any>(action: string, payload?: any, params?: any) =>
    axios.post(`${API_URL}/actions/agent`, (payload ? {payload} : null), {params: Object.assign({action}, params)})
        .then((res) => res && res.data && (res.data.payload || {}) as T);

const executeFileServerAction = <T = any>(action: string, payload?: any, params?: any) =>
    axios.post(`${envConfig.fileServerApiUrl}/file-server/actions/agent`,
        (payload ? {payload} : null), {params: Object.assign({action}, params)}
    ).then((res) => res && res.data && (res.data.payload || {}) as T);

const joinSession = (sessionId: string) => executeAction(actionTypes.JOIN_SESSION, {sessionId});

export type SessionParams = {
    sessionId: string;
    participantId: string;
    token: string;
}

/** Session Actions */
const getActiveSessionState = (params: SessionParams, payload?: {settings?: boolean}) =>
    executeAction(actionTypes.GET_STATE, payload, params);

const startOnHold = (params: SessionParams) => executeAction(actionTypes.START_ON_HOLD, null, params);

const reassignSession = (params: SessionParams, payload: Partial<ReassignDetails>) =>
    executeAction(actionTypes.REASSIGN_SESSION, payload, params);

const stopOnHold = (params: SessionParams) => executeAction(actionTypes.STOP_ON_HOLD, null, params);

const notifyAgentSession = (params: SessionParams, payload: {name: string, data: any}) =>
    executeAction(actionTypes.NOTIFY, payload, params);

const getScreenShot = (params: SessionParams, name: string) =>
    executeFileServerAction(actionTypes.GET_SCREENSHOT, {name}, params);

const deleteScreenShot = (params: SessionParams, name: string) =>
    executeFileServerAction(actionTypes.DELETE_SCREENSHOT, {name}, params);

const downloadScreenShot = (params: SessionParams, name: string) =>
    executeFileServerAction(actionTypes.DOWNLOAD_SCREENSHOT, {name}, params);

const uploadScreenShot = (params: SessionParams, base64: string, name: string, participantId?: string) =>
    executeFileServerAction(actionTypes.UPLOAD_SCREENSHOT, {base64, name, participantId}, params);

const startActivity = (params: SessionParams, name: string, data?: any) => executeAction(actionTypes.START_ACTIVITY, {name, data}, params);

const restartActivity = (params: SessionParams, id: string) => executeAction(actionTypes.RESTART_ACTIVITY, {id}, params);

const addActivityData = (params: SessionParams, id: string, data: any) => executeAction(actionTypes.ADD_ACTIVITY_DATA, {id, data}, params);

const getActivityFile = (params: SessionParams, payload: {fileName: string, activityId: string}) =>
    executeFileServerAction(actionTypes.GET_ACTIVITY_FILE, payload, params);

const stopActivity = (params: SessionParams, id: string) => executeAction(actionTypes.STOP_ACTIVITY, {id}, params);

const sendParticipantInviteNotification = (params: SessionParams, inviteParams: {participantId: string, type: string, contact: string}) =>
    executeAction(actionTypes.SEND_PARTICIPANT_INVITE_NOTIFICATION, inviteParams, params);

const createParticipantAsyncRequest = (
    params: SessionParams, payload: {participantId: string, requestType: AsyncRequestTypeName, data?: any}
) =>
    executeAction(actionTypes.CREATE_ASYNC_REQUEST, payload, params);

const updateParticipantAsyncRequest = (params: SessionParams, payload: {
    participantId: string, requestType: AsyncRequestTypeName, status: AsyncRequestStatusName, data: any
}) => executeAction(actionTypes.UPDATE_ASYNC_REQUEST, payload, params);

const getParticipantAsyncRequestScreenshot = (params: SessionParams, payload: {
    requestType: AsyncRequestTypeName, participantId: string
}) => executeFileServerAction(actionTypes.GET_ASYNC_REQUEST_SCREENSHOT, payload, params);

const resetParticipantAsyncRequest = (params: SessionParams, payload: {
    requestType: AsyncRequestTypeName, participantId: string
}) => executeAction(actionTypes.RESET_ASYNC_REQUEST, payload, params);

const endCall = (params: SessionParams, details: any) => executeAction(actionTypes.END_CALL, details, params);

const getVirtualBackgrounds = (params: SessionParams) =>
    executeAction<VirtialBackgroundsData>(actionTypes.GET_VIRTUAL_BACKGROUNDS, null, params);

const updateVirtualBackground = (config: VirtualBackgroundConfig, params: SessionParams) =>
    executeAction(actionTypes.UPDATE_VIRTUAL_BACKGROUND, {config}, params);

const getAssignmentAvailability = (params: SessionParams, payload: {groupId?: string, subGroupId?: string}) =>
    executeAction<AssignmentAvailabilityData>(actionTypes.GET_ASSIGNMENT_AVAILABILITY, payload, params);

const getUsersAvailability = (params: SessionParams) =>
    executeAction<{
        users: User[], onsiteUsersEids: number[], inSessionUsersEids: number[]
    }>(actionTypes.GET_USERS_AVAILABILITY, null, params);

const addFeedback = (params: SessionParams, payload: {feedback: SessionFeedback}) =>
    executeAction(actionTypes.ADD_FEEDBACK, payload, params);

const getAiSuggestions = (params: SessionParams, payload: {activityName: SessionActivityName}) =>
    executeAction(actionTypes.GET_AI_SUGGESTIONS, payload, params);

const getTextChatHistory = (params: SessionParams, page = 1) =>
    executeAction<PaginatedData<SessionHistory>>(actionTypes.GET_CLIENT_TEXT_CHAT_HISTORY, {page}, params);

const createClientConnect = (params: SessionParams, payload: Partial<ClientConnect>) =>
    executeAction(actionTypes.CREATE_CLIENT_CONNECT, payload, params);

const deleteClientConnect = (params: SessionParams) =>
    executeAction(actionTypes.DELETE_CLIENT_CONNECT, null, params);

const addNote = (params: SessionParams, payload: {message: string, access: SessionNoteAccess}) =>
    executeAction(actionTypes.ADD_NOTE, payload, params);

const deleteNote = (params: SessionParams, payload: {id: string}) =>
    executeAction(actionTypes.DELETE_NOTE, payload, params);

const getPrevSessionsNotes = (params: SessionParams, page: number) =>
    executeAction<PaginatedData<SessionHistory>>(actionTypes.GET_PREV_NOTES, {page}, params);

const changeAssignment = (params: SessionParams, assignment: AccountAssignmentInfo) =>
    executeAction(actionTypes.CHANGE_ASSIGNMENT, {assignment}, params);

const updateParticipantData = (params: SessionParams, payload: {participantId: string, updates: any}) =>
    executeAction(actionTypes.UPDATE_PARTICIPANT_DATA, payload, params);

const leaveSession = (params: SessionParams) => executeAction(actionTypes.LEAVE_SESSION, null, params);

const inviteParticipants = (
    params: SessionParams, payload: {participants: AnyParticipant[]}
): Promise<{participant: AnyParticipant}[]> =>
    executeAction(actionTypes.INVITE_PARTICIPANTS, payload, params);

const removeParticipant = (params: SessionParams, participantId: string) =>
    executeAction(actionTypes.REMOVE_PARTICIPANT, {participantId}, params);

const deescalate = (params: SessionParams, data: {context: string}) =>
    executeAction(actionTypes.DEESCALATE, data, params);

const askAgentChatbot = (params: SessionParams, payload: {activityId: string, question: string}) =>
    executeAction<{message: TextChatMessage}>(actionTypes.ASK_AGENT_CHATBOT, payload, params);

export const agentActionsApi = {
    joinSession,
    getActiveSessionState,
    reassignSession,
    endCall,
    getScreenShot,
    deleteScreenShot,
    downloadScreenShot,
    uploadScreenShot,
    notifyAgentSession,
    startActivity,
    restartActivity,
    addActivityData,
    getActivityFile,
    stopActivity,
    startOnHold,
    stopOnHold,
    createParticipantAsyncRequest,
    updateParticipantAsyncRequest,
    getParticipantAsyncRequestScreenshot,
    resetParticipantAsyncRequest,
    getVirtualBackgrounds,
    updateVirtualBackground,
    sendParticipantInviteNotification,
    addFeedback,
    getAiSuggestions,
    getTextChatHistory,
    createClientConnect,
    deleteClientConnect,
    addNote,
    deleteNote,
    getPrevSessionsNotes,
    inviteParticipants,
    removeParticipant,
    changeAssignment,
    updateParticipantData,
    getAssignmentAvailability,
    getUsersAvailability,
    leaveSession,
    deescalate,
    askAgentChatbot,
};
